/**
 * compoundRanker — produces a deterministic ordered ranking of loaded
 * compounds for the results-interpretation layer.
 *
 * Consumes the output of `serializeWorkspace.getCompoundData()`
 * (anonymized compound records with id, atomCount, energies,
 * properties). Optionally accepts enrichment data (flags, interactions)
 * when the caller has run `structuralAlertScanner` / ProLIF upstream.
 *
 * Determinism is a hard requirement per the `ranked-hit-display`
 * criterion: identical input + identical weights MUST produce
 * byte-identical ordering across repeated invocations.
 *
 * @module gibbs/results/compoundRanker
 */

/**
 * @typedef {Object} CompoundDatum
 * @property {number} id - 1-based anonymized index
 * @property {boolean} [isActive]
 * @property {number} [atomCount]
 * @property {Object} [energies] - {vdw, electrostatic, total, dockingScore, gninaScore}
 * @property {Object} [properties]
 * @property {string[]} [flags] - Structural-alert identifiers from scanSmilesForAlerts
 * @property {number} [interactionCount] - Total interactions from ProLIF
 */

/**
 * @typedef {Object} RankedCompound
 * @property {number} rank - 1-based
 * @property {number} compoundId - Matches CompoundDatum.id
 * @property {number|null} primaryScore - Promoted energy score; null if unavailable
 * @property {Object} secondaryScores - Remaining energy fields + metadata
 * @property {string[]} flags
 * @property {number} interactionCount
 */

/**
 * @typedef {Object} RankerWeights
 * @property {string[]} primaryScorePreference - Energy field names in
 *   descending preference order. First non-null is promoted to
 *   primaryScore; lowest value = best rank.
 * @property {boolean} tiebreakByFlagCount - Fewer flags = higher rank on
 *   primaryScore ties.
 */

/** @type {RankerWeights} */
export const defaultWeights = Object.freeze({
    primaryScorePreference: ['gninaScore', 'dockingScore', 'total'],
    tiebreakByFlagCount: true,
});

/**
 * Extract the promoted primary score from a compound's energies object,
 * using the preference order in weights. Returns null when no listed
 * field has a numeric value.
 *
 * @param {Object|undefined} energies
 * @param {string[]} preference
 * @returns {number|null}
 */
function promotePrimaryScore(energies, preference) {
    if (!energies) return null;
    for (const field of preference) {
        const v = energies[field];
        if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return null;
}

/**
 * Rank a list of compounds by promoted primary score, with deterministic
 * tiebreaking.
 *
 * Ranking rules:
 * 1. Compounds with a non-null primaryScore rank above those without.
 * 2. Among compounds with primaryScore, lower value = better rank.
 * 3. Tiebreak: fewer flags wins (if tiebreakByFlagCount enabled).
 * 4. Final tiebreak: lower compoundId wins (ensures stability).
 *
 * @param {CompoundDatum[]} compoundData
 * @param {RankerWeights} [weights=defaultWeights]
 * @returns {RankedCompound[]}
 */
export function rankCompounds(compoundData, weights = defaultWeights) {
    if (!Array.isArray(compoundData) || compoundData.length === 0) {
        return [];
    }
    const w = weights || defaultWeights;
    const preference = w.primaryScorePreference || defaultWeights.primaryScorePreference;

    const entries = compoundData.map((c) => {
        const primaryScore = promotePrimaryScore(c.energies, preference);
        // Distinguish "scanned and clean" (flags: []) from "scanner
        // unavailable / not attempted" (flags: undefined or null).
        // The first signals confidence; the second triggers the '?'
        // glyph in ResultsTable. Pre-fix the ranker normalized both
        // to [], which hid the unavailable case behind a green
        // checkmark — bad UX when toxicophore scanning had failed.
        // From `gibbs-results-interpretation-layer/followups.md`.
        let flags;
        if (c.flags === undefined || c.flags === null) {
            flags = null;
        } else if (Array.isArray(c.flags)) {
            flags = c.flags;
        } else {
            flags = [];
        }
        const interactionCount = typeof c.interactionCount === 'number'
            && Number.isFinite(c.interactionCount)
            ? c.interactionCount
            : 0;
        const secondaryScores = {};
        if (c.energies) {
            for (const [k, v] of Object.entries(c.energies)) {
                secondaryScores[k] = v;
            }
        }
        if (primaryScore === null) {
            secondaryScores.note = 'No energies computed';
        }
        return {
            compoundId: c.id,
            primaryScore,
            secondaryScores,
            flags,
            interactionCount,
        };
    });

    // Partition: compounds with primaryScore first, then nulls.
    const scored = entries.filter((e) => e.primaryScore !== null);
    const unscored = entries.filter((e) => e.primaryScore === null);

    // Sort scored: primaryScore ASC, then flag count ASC, then compoundId ASC.
    // Treat null `flags` (scanner unavailable) as having an unknown
    // count — slot AFTER compounds whose scanner ran (preserve known-
    // good results in front when scores are tied). A null-vs-null
    // comparison falls through to compoundId for stability.
    scored.sort((a, b) => {
        if (a.primaryScore !== b.primaryScore) return a.primaryScore - b.primaryScore;
        if (w.tiebreakByFlagCount) {
            const aFlagCount = a.flags === null ? Infinity : a.flags.length;
            const bFlagCount = b.flags === null ? Infinity : b.flags.length;
            if (aFlagCount !== bFlagCount) return aFlagCount - bFlagCount;
        }
        return a.compoundId - b.compoundId;
    });

    // Sort unscored: compoundId ASC (pure stability)
    unscored.sort((a, b) => a.compoundId - b.compoundId);

    const combined = [...scored, ...unscored];
    return combined.map((e, i) => ({
        rank: i + 1,
        ...e,
    }));
}
